// eslint:disable:max-func-body-length
import * as React from 'react';
import { Options, LoadOptions } from '../types';
import { InputControlContext, InputControlContextProps } from '../input-control-context';
import { SelectInput } from './select-input';
import { SelectItem, SelectType, SelectCommonProps } from './types';
import { SelectedOptions } from './selected-options/selected-options';
import { CloseOnBlur } from '../close-on-blur';
import { scrollIntoViewIfNecessary } from './scroll-into-view-if-necessary';
import debounce from 'lodash/debounce';
import { isHierarchy, isMultiple, isSingle } from './select-type';
import { KeyboardSupport } from './keyboard-support';
import styles from './select-base-component.scss';
import { numberOfLevels } from './constants';
import { ANIMATE_OUT_SPEED } from 'ui/constants';
import { KeepFocusOnElement } from 'ui/common/keep-focus-on-element';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { OptionsContainer } from 'ui/common/select/options-container';
import isNumber from 'lodash/isnumber';
import { Checkbox } from 'ui/checkbox';

import type { JSX } from "react";

export const getValueItem = (valueItems: SelectItem[]) => {
  return !!valueItems && !!valueItems.length ? valueItems[0] : undefined;
};

export const achievedMinLength = (value: string, minLength: number = 2) => {
  const normalizedStringLength = value && Math.max(...value.split(' ').map(v => v.length));
  return !!value && normalizedStringLength >= minLength;
};

/**
 * SelectSingle component let you choose one option from list of options.
 *
 * > It supports common InputControlContext component.
 */
export interface SelectBaseComponentProps
  extends InputControlContextProps,
  SelectCommonProps,
  Options<number | string> {
  selectType: SelectType;

  placeholder?: string;
  readOnly?: boolean;
  readOnlyItemIds?: number[];
  focus?: boolean;

  valueItems?: SelectItem[];
  onChange: (data: SelectItem | SelectItem[]) => void;

  hideSearchInput?: boolean;

  // hierarchy props
  minLevel?: number;
  levelsNotAllowed?: boolean;
  leafNodeOnly?: boolean;
  descriptionAllLevels?: boolean;
  resetPreviousValueWhenEmpty?: boolean;
  showBackgroundColour?: boolean;
  showSpinnerLabel?: boolean;
  enableMultilineValidation?: boolean;
  displayCategories?: boolean;
  keepSearchQuery?: boolean;
}

export interface SelectPrevState {
  isOpen?: boolean;
  longDescription?: boolean;
  valueItems?: SelectItem[];
}

export enum IncludeDisabledState {
  init,
  checked,
  unchecked
}

export interface SelectState {
  inputValue?: string;
  isOpen: boolean;
  isClosing: boolean;
  searchMode: boolean;
  prev: SelectPrevState;
  selectedCategoryId: number | undefined;
  includeDisabled: IncludeDisabledState;
}

export class SelectBaseComponent extends React.Component<SelectBaseComponentProps, SelectState> {
  private debounceOnLoad: any;
  private timeoutHandle: any;
  private inputRef: HTMLTextAreaElement;
  private containerRef = React.createRef<HTMLDivElement>();

  public static defaultProps: Partial<SelectBaseComponentProps> = {
    minLength: 2,
    showIcons: true,
    loadOptions: {},
    loadCategoryOptions: {},
    longDescription: true,
    initiallyShowLongDescription: false,
    isOpen: false,
    readOnly: false,
    readOnlyItemIds: [],
    minLevel: 0,
    levelsNotAllowed: false,
    descriptionAllLevels: false,
    leafNodeOnly: false,
    hideSearchInput: false,
    hideAlphabeticIndex: false,
    onLoadOptionsDelay: 500,
    resetPreviousValueWhenEmpty: false,
    includeDisabled: false
  };

  public static getDerivedStateFromProps(props: SelectBaseComponentProps, state: SelectState) {
    const prev = {
      longDescription: props.longDescription,
      isOpen: props.isOpen,
      valueItems: props.valueItems
    };

    const previousValueItem = getValueItem(state.prev.valueItems);
    const previousDescription = previousValueItem?.description ?? '';
    const nextValueItem = getValueItem(props.valueItems);
    const nextDescription = nextValueItem?.description ?? '';
    const descriptionChanged = previousDescription !== nextDescription;

    return {
      ...(!isMultiple(props.selectType) && !state.searchMode && descriptionChanged
        ? { inputValue: nextDescription }
        : {}),
      ...(props.isOpen !== state.prev.isOpen ? { isOpen: props.isOpen } : {}),
      ...(props.longDescription !== state.prev.longDescription
        ? { showLongDescription: props.longDescription }
        : {}),
      prev,
      selectedCategoryId: state.selectedCategoryId !== props.categoryId && props.categoryId
        ? props.categoryId : state.selectedCategoryId
    };
  }

  constructor(props: SelectBaseComponentProps) {
    super(props);

    const valueItem = getValueItem(props.valueItems);

    this.state = {
      isOpen: props.isOpen,
      inputValue: this.isMultiple ? '' : valueItem?.description ?? '',
      isClosing: false,
      searchMode: false,
      prev: {
        longDescription: props.longDescription,
        isOpen: props.isOpen,
        valueItems: props.valueItems
      },
      selectedCategoryId: props.categoryId,
      includeDisabled: IncludeDisabledState.init
    };

    this.debounceOnLoad = debounce((query: string) => {
      this.loadOptions({ query: query?.trim?.() });
    }, this.props.onLoadOptionsDelay);
  }

  private getInputRef = (node: HTMLTextAreaElement) => {
    this.inputRef = node;
  };

  get isMultiple(): boolean {
    return isMultiple(this.props.selectType);
  }
  get isSingle(): boolean {
    return isSingle(this.props.selectType);
  }
  get isHierarchy(): boolean {
    return isHierarchy(this.props.selectType);
  }
  get shouldRenderAsOpen() {
    return this.props.isOpen || this.props.alwaysOpen;
  }

  private achievedMinLengthCondition = (inputValue: string) => {
    return achievedMinLength(inputValue, this.props.minLength);
  };

  public componentDidMount() {
    if (this.shouldRenderAsOpen) {
      this.loadInitialOptionsOrCategoryOptions();
    }
  }

  public componentWillUnmount() {
    this.inputRef = null;
    clearTimeout(this.timeoutHandle);
  }

  private loadInitialOptionsOrCategoryOptions = () => {
    if (this.props.displayCategories) {
      this.loadInitialCategoryOptions();
    }
    else {
      this.loadInitialOptions();
    }
  };

  private loadOptions = (loadOptions: LoadOptions = {}) => {
    const categoryId = isNumber(this.state.selectedCategoryId) && {
      categoryId: this.state.selectedCategoryId
    };

    const leafNodesOnly = this.props.leafNodeOnly === true;

    const dirtyIncludeDisabled = this.state.includeDisabled !== IncludeDisabledState.init;

    const includeDisabled = !!this.props.includeDisabledLabel && {
      includeDisabled: this.state.includeDisabled === IncludeDisabledState.checked
    };

    const parentId = !!dirtyIncludeDisabled
      && isDefinedIncludingZero(this.props.loadOptions?.parentId) && {
      parentId: this.props.loadOptions?.parentId
    };

    const optionDomainId = this.props.loadOptions?.optionDomainId !== undefined && {
      optionDomainId: this.props.loadOptions.optionDomainId
    };

    const siblingId = !!dirtyIncludeDisabled
      && isDefinedIncludingZero(this.props.loadOptions?.siblingId) && {
      siblingId: this.props.loadOptions?.siblingId
    };

    const query = !!dirtyIncludeDisabled
      && !!this.state.searchMode
      && !!this.state.inputValue && {
      query: this.state.inputValue
    };

    if (this.props.searchRequired) {
      if (this.achievedMinLengthCondition(this.state.inputValue)) {
        this.props.onLoadOptions?.({
          query: this.state.inputValue,
          ...categoryId,
          ...optionDomainId,
          ...(leafNodesOnly && { leafNodesOnly }),
          ...includeDisabled,
          ...parentId
        });
      }
    } else {
      this.props.onLoadOptions?.({
        ...loadOptions,
        ...categoryId,
        ...optionDomainId,
        ...(leafNodesOnly && { leafNodesOnly }),
        ...includeDisabled,
        ...parentId,
        ...siblingId,
        ...query
      });
    }
    !this.state.isOpen && this.setState({ isOpen: true });
  };

  private loadOptionsWithLevels = (options?: LoadOptions) => {
    const { levelsNotAllowed, selectType, allowQuerySearch } = this.props;
    const levels = !levelsNotAllowed ? numberOfLevels : undefined;
    const query = allowQuerySearch && this.state.inputValue && { query: this.state.inputValue };

    const showLevels = selectType === SelectType.singleHierarchy && !levelsNotAllowed ||
      selectType === SelectType.multipleHierarchy;

    if (showLevels) {
      this.loadOptions({
        levels,
        ...query,
        ...options
      });
    } else {
      this.loadOptions({
        ...options
      });
    }
  };

  private loadInitialOptions = () => {
    const { levelsNotAllowed } = this.props;
    const query = this.props?.keepSearchQuery && this.state.inputValue
      && { query: this.state.inputValue };

    if (this.props.selectType === SelectType.singleHierarchy && !levelsNotAllowed) {
      // Load the tree branch containing selected value if it's a singleHierarchy.
      const valueItem = getValueItem(this.props.valueItems);
      const hasValue = !!valueItem && isDefinedIncludingZero(valueItem.value);

      this.loadOptionsWithLevels({
        siblingId: hasValue ? Number(valueItem.value) : 0,
        ...query
      });
    } else {
      this.loadOptionsWithLevels({
        ...query
      });
    }
  };

  private loadCategoryOptions = (args: LoadOptions = {}) => {

    this.props.onLoadCategoryOptions({
      ...args,
      ...(!!this.props.includeDisabledLabel && { includeDisabled: !!this.state.includeDisabled }),
      ...(isNumber(this.state.selectedCategoryId) && { categoryId: this.state.selectedCategoryId }),
      ...(!!this.state.searchMode && !!this.state.inputValue && {
        query: this.state.inputValue
      })
    });
    !this.state.isOpen && this.setState({ isOpen: true });
  };

  private loadInitialCategoryOptions = () => {
    const { valueItems } = this.props;
    const setCategoryIdFromSelectedValue = valueItems?.length === 1 &&
      this.isSingle &&
      valueItems[0].categoryId !== 0 && valueItems[0].categoryId !== this.state.selectedCategoryId;

    if (setCategoryIdFromSelectedValue) {
      const categoryId = this.props.valueItems[0].categoryId;
      this.setState({ selectedCategoryId: categoryId }, () => {
        categoryId === undefined ? this.loadCategoryOptions() : this.loadInitialOptions();
      });
      this.loadCategoryOptions();
    }
    else if (this.state.selectedCategoryId) {
      const siblingIdParam = this.isHierarchy && {
        siblingId: this.props.valueItems[0].categoryId
      };
      // this.setCategoryId automatically runs this, so we only need to
      // run it explicitly if we have a category already selected
      this.loadInitialOptions();
      this.loadCategoryOptions({
        ...siblingIdParam
      });
    }
    else {
      this.loadInitialOptions();
      this.loadCategoryOptions();
    }
  };

  private isEmpty = (value: string) => {
    return (value?.trim?.() ?? '') === '';
  };

  private handleInputChange = (value: string) => {
    this.setState(
      {
        inputValue: value,
        searchMode: true
      }
    );

    if (this.achievedMinLengthCondition(value)) {
      this.debounceOnLoad(value);
    } else {
      this.debounceOnLoad.cancel();
      if (this.isEmpty(value)) {
        if (this.isSingle) {
          if (this.props.resetPreviousValueWhenEmpty) {
            this.debounceOnLoad(value);
            this.handleBlurComponent();
          } else {
            this.props.onChange(undefined);
          }
        }
        if (this.props.alwaysOpen) {
          this.debounceOnLoad('');
        } else {
          this.onCloseOptions();
        }
      }
      else {
        if (!this.props.searchRequired && value?.length) {
          // We have backspaced but not completely cleared search input
          // We have not achieved minLength, so close the expander.
          this.onCloseOptions();
        }
      }
    }
  };

  private onCloseOptions = (moveFocus: boolean = true) => {
    if (this.state.isOpen && !this.props.alwaysOpen) {
      this.setState({ isClosing: true, selectedCategoryId: this.props.displayCategories ? undefined : this.state.selectedCategoryId });
      if (this.inputRef && moveFocus) {
        this.inputRef.focus();
      }

      clearTimeout(this.timeoutHandle);
      this.timeoutHandle = setTimeout(() => {
        this.setState({
          isOpen: false,
          isClosing: false,
          includeDisabled: IncludeDisabledState.init
        });
        !!this.props.clearLoadOptions && this.props.clearLoadOptions();
        scrollIntoViewIfNecessary(this.containerRef.current);
      }, ANIMATE_OUT_SPEED);
    }
  };

  private onClickedOpenOptions = () => {
    if (this.state.isOpen) {
      this.onCloseOptions();
    } else {
      this.loadInitialOptionsOrCategoryOptions();
    }
  };

  private onSelectOption = (item: SelectItem) => {
    const { valueItems = [], onChange } = this.props;

    if (this.isMultiple) {
      const selectedItemIndex = valueItems.find((i) => i.value === item.value);
      if (selectedItemIndex) {
        onChange([...valueItems.filter((i) => i.value !== item.value)]);
      } else {
        onChange([...valueItems, item]);
      }
    } else {
      this.setState({
        inputValue: item.description,
        isClosing: true,
        searchMode: false
      });

      if (this.inputRef) {
        this.inputRef.focus();
      }

      onChange(item);
      this.onCloseOptions(true);
    }
  };

  private setCategoryId = (categoryId: number) => {
    this.setState({ selectedCategoryId: categoryId }, () => {
      categoryId === undefined ? this.loadCategoryOptions() : this.loadOptionsWithLevels();
    });
  };

  private handleBlurComponent = () => {
    const valueItem = getValueItem(this.props.valueItems);
    if (!this.props.alwaysOpen) {
      this.setState(
        {
          searchMode: false,
          inputValue: this.isMultiple ? '' : valueItem?.description ?? ''
        },
        () => {
          this.onCloseOptions(false);
        }
      );
    }
  };

  private handleBlurAndCancelOnLoad = () => {
    this.debounceOnLoad.cancel();
    this.handleBlurComponent();
  };

  private handleIncludeDisabled = () => {
    const newIncludeDisabledState = {
      [IncludeDisabledState.init]: IncludeDisabledState.checked,
      [IncludeDisabledState.unchecked]: IncludeDisabledState.checked,
      [IncludeDisabledState.checked]: IncludeDisabledState.unchecked
    };

    this.setState(
      { includeDisabled: newIncludeDisabledState[this.state.includeDisabled] },
      () => {
        !!this.props.displayCategories && this.loadCategoryOptions();
        this.loadOptions();
      }
    );
  };

  private includeDisabledCheckbox = (): JSX.Element => {
    return !!this.props.includeDisabledLabel
      && <Checkbox
        className={styles.includeDisabled}
        componentId={undefined}
        value={this.state.includeDisabled === IncludeDisabledState.checked ? 1 : 0}
        label={{ text: this.props.includeDisabledLabel, visible: true }}
        onChange={this.handleIncludeDisabled} />;
  };

  public render() {

    const {
      valueItems, placeholder, focus, readOnly, readOnlyItemIds,
      hideSearchInput, disabled, alwaysOpen, shouldSortSelectedItems = true,
      validation, componentId, locale
    } = this.props;

    const { inputValue, selectedCategoryId, isOpen, isClosing } = this.state;

    const thinnerWrapperClass =
      readOnly && this.isMultiple && valueItems.length > 0 ? styles.wrapperThin : '';

    return (
      <InputControlContext {...this.props}>
        <CloseOnBlur onBlur={this.handleBlurAndCancelOnLoad}>
          <KeepFocusOnElement>
            <KeyboardSupport
              inputValue={inputValue}
              closeOptions={this.onCloseOptions}
              loadAndOpenOptions={this.loadOptions}>
              <div ref={this.containerRef} className={`input-wrapper select-base-component ${thinnerWrapperClass}`}>
                {this.isMultiple && (
                  <SelectedOptions
                    componentId={componentId}
                    items={valueItems}
                    shouldSortSelectedItems={shouldSortSelectedItems}
                    loadOptions={this.loadOptions}
                    onChange={this.props.onChange}
                    readOnly={readOnly}
                    readOnlyItemIds={readOnlyItemIds}
                    validation={validation}
                    locale={locale} />
                )}
                {!hideSearchInput && (
                  <SelectInput
                    placeholder={!readOnly ? placeholder : undefined}
                    inputValue={inputValue}
                    onChange={this.handleInputChange}
                    focus={focus}
                    readOnly={readOnly}
                    disabled={disabled}
                    onOpenOptions={alwaysOpen ? null : this.onClickedOpenOptions}
                    hasSelectedOptions={this.isMultiple && valueItems.length > 0}
                    inputRef={this.getInputRef}
                  />
                )}
              </div>
              <OptionsContainer
                {...this.props}
                inputValue={inputValue}
                onSelectOption={this.onSelectOption}
                onCloseOptions={this.onCloseOptions}
                setCategoryId={this.setCategoryId}
                categoryId={selectedCategoryId}
                isOpen={isOpen}
                isClosing={isClosing}
                includeDisabled={this.state.includeDisabled === IncludeDisabledState.checked}
                includeDisabledCheckbox={this.includeDisabledCheckbox()}
              />
            </KeyboardSupport>
          </KeepFocusOnElement>
        </CloseOnBlur>
      </InputControlContext>
    );
  }
}
